import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  type Classification,
  type EngagementSummary,
  type OrganisationSummary,
  type Prospect,
  type Person,
} from "../lib/api";
import { roleLabel, useSession } from "../lib/session";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  Select,
  SkeletonRows,
} from "../components/primitives";
import { classificationLabel, day } from "../lib/format";

/**
 * Engagements — the index, and the place one begins.
 *
 * `createEngagement` had existed in the domain layer since Slice 1, tested,
 * with no route and no screen in front of it, so the deployed platform could
 * be signed into and not used. This is the other half.
 *
 * The index is also what replaces `useFirstEngagement`'s hardcoded fixture
 * uuid in App.tsx. That stub said so in a comment and was honest about it;
 * it is only now that there is an endpoint for it to stop being a stub.
 *
 * Creating is Super Admin or Principal Partner — `engagement_insert` says so
 * and the server refuses everyone else, including a Lead with full authority
 * over an engagement they already run. The form is hidden from people who
 * cannot use it, which is courtesy; the refusal is the database's.
 */
export function Engagements() {
  const { me } = useSession();
  const { resource, reload } = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );

  /**
   * `?prospect=<id>`, which Catch sends when somebody turns a finished
   * introductory call into a matter.
   *
   * It opens the form and preselects the prospect, and that is all it does —
   * the conversion still happens inside `POST /api/engagements`, in one
   * transaction with the engagement, exactly as it does when the prospect is
   * chosen from the dropdown by hand. A link that created a client on arrival
   * would be a second way for one relationship to become two clients, which is
   * the whole reason Catch has no convert button of its own.
   *
   * Read once into state rather than used live: the person is about to edit
   * this form, and a URL they then navigate within should not reach back in
   * and reset their choice. The parameter is cleared from the address bar for
   * the same reason — so a refresh halfway through does not silently re-pick a
   * prospect they had changed their mind about.
   */
  const [params, setParams] = useSearchParams();
  const fromCatch = params.get("prospect");

  const [starting, setStarting] = useState(Boolean(fromCatch));
  const [presetProspectId] = useState(fromCatch ?? "");

  useEffect(() => {
    if (!fromCatch) return;
    const next = new URLSearchParams(params);
    next.delete("prospect");
    setParams(next, { replace: true });
  }, [fromCatch, params, setParams]);

  /**
   * Who may start one, and why this is a role check rather than a flag
   * from the server.
   *
   * `engagement_insert` (004) is
   * `app.current_role_of() IN ('super_admin','principal_partner')` — role
   * and nothing else. No reach, no membership, nothing the session cannot
   * see. So unlike `mayManage`, which the server has to answer because it
   * is role AND reach, this one is exactly derivable here and a round
   * trip would add nothing.
   *
   * It was ungated: every consultant and every Lead was shown "Start an
   * engagement", and the server refused all of them. Same defect as the
   * engagement overview had, in a different room.
   */
  const mayStart =
    me?.role === "super_admin" || me?.role === "principal_partner";

  return (
    <AppShell>
      <ScreenHead title="Engagements" />

      {mayStart &&
        (starting ? (
          <NewEngagement
            presetProspectId={presetProspectId}
            onDone={() => {
              setStarting(false);
              reload();
            }}
            onCancel={() => setStarting(false)}
          />
        ) : (
          <div className="mt-6">
            <Button tone="primary" onClick={() => setStarting(true)}>
              Start an engagement
            </Button>
          </div>
        ))}

      <section className="mt-10">
        <Label as="h2">Engagements available to you</Label>

        {resource.state === "loading" && <SkeletonRows rows={4} />}

        {resource.state === "failed" && (
          <Failed
            message={resource.message}
            correlationId={resource.correlationId}
          />
        )}

        {resource.state === "ready" && resource.data.length === 0 && (
          <Empty title="No engagements yet">
            Start an engagement to organise its work, deliverables, files and
            client portal in one place.
          </Empty>
        )}

        {resource.state === "ready" &&
          resource.data.map((e) => <Row key={e.id} engagement={e} />)}
      </section>
    </AppShell>
  );
}

/**
 * Not a single wrapping Link: the row carries two destinations, and an
 * anchor inside an anchor is invalid HTML that browsers resolve by guessing.
 */
function Row({ engagement }: { engagement: EngagementSummary }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line py-4 transition-colors hover:bg-ink/[0.03]">
      <Link to={`/engagements/${engagement.id}`} className="min-w-0 flex-1">
        <p className="text-base text-ink">
          {engagement.name}{" "}
          <span className="text-secondary">· {engagement.organisation}</span>
        </p>
        <p className="mt-1 font-mono text-xs text-secondary">
          {engagement.reference}
        </p>
      </Link>

      <div className="flex items-center gap-4">
        <Mark>{classificationLabel(engagement.classification)}</Mark>
        <span className="text-sm text-secondary">{engagement.status}</span>
        {engagement.startedOn && (
          <span className="text-sm text-secondary">
            {day(engagement.startedOn)}
          </span>
        )}
        <Link
          to={`/engagements/${engagement.id}/team`}
          className="text-sm text-secondary underline-offset-4 hover:text-ink hover:underline"
        >
          Team
        </Link>
      </div>
    </div>
  );
}

/** Sentinel for the dropdown's add-a-client option. */
const NEW_CLIENT = "__new__";

/**
 * Prospect ids and organisation ids are both uuids, and one `<select>` holds
 * both. The prefix is what keeps them apart — without it the form would have
 * to guess which table an id came from, and guessing wrong files a matter
 * against the wrong client, which PRODUCT.md names as the most expensive
 * mistake available here.
 */
const PROSPECT_PREFIX = "prospect:";

const CLASSIFICATIONS: Classification[] = [
  "internal",
  "client_visible",
  "restricted",
  "highly_restricted",
];

function NewEngagement({
  presetProspectId = "",
  onDone,
  onCancel,
}: {
  /** A prospect chosen in Catch, already selected on arrival. */
  presetProspectId?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const orgs = useResource<OrganisationSummary[]>(
    () => api.get<OrganisationSummary[]>("/api/organisations"),
    [],
  );
  const people = useResource<Person[]>(
    () => api.get<Person[]>("/api/people"),
    [],
  );
  // Catch's pipeline (050). Only the ones still open can be taken on: a
  // converted prospect is already a client and appears above, and a declined
  // one is refused by app.convert_prospect until somebody reopens it. Offering
  // either would be a control whose refusal arrives at the end of a filled-in
  // form.
  const prospects = useResource<Prospect[]>(
    () => api.get<Prospect[]>("/api/prospects"),
    [],
  );
  const openProspects =
    prospects.resource.state === "ready"
      ? prospects.resource.data.filter((p) => p.status === "open")
      : [];


  // Exactly one of these is set. The server refuses both or neither, and so
  // does the domain layer — the picker simply cannot express it.
  const [organisationId, setOrganisationId] = useState("");
  const [prospectId, setProspectId] = useState(presetProspectId);
  const [name, setName] = useState("");
  const [addingClient, setAddingClient] = useState(false);
  const [leadUserId, setLeadUserId] = useState("");
  const [classification, setClassification] =
    useState<Classification>("internal");

  /**
   * A preset prospect that is no longer selectable gets dropped.
   *
   * Catch only offers the link for an open prospect, so the normal path cannot
   * produce this. A stale link can: the back button after starting the matter,
   * a URL somebody kept, or two people working the same prospect. Without this
   * the id stays in state while the dropdown has no matching option — so the
   * control looks empty, reads as "no client chosen", and the submit then fails
   * with "already a client" from the server. Clearing it puts the form in the
   * state it appears to be in.
   */
  useEffect(() => {
    if (!prospectId || prospects.resource.state !== "ready") return;
    if (!openProspects.some((p) => p.id === prospectId)) setProspectId("");
  }, [prospectId, prospects.resource.state, openProspects]);

  const { invoke, pending, refusal } = useAction(async () => {
    await api.post<{ id: string }>("/api/engagements", {
      // One or the other. A prospect is converted into a client inside the
      // same transaction that creates the engagement — see 050 — so this is
      // the single act that turns an introductory call into a matter.
      ...(prospectId ? { prospectId } : { organisationId }),
      // No reference. It is minted as ARIKCO-<CLIENT>-<YY>-<NNN> by the
      // database (migration 025) — typing it was how a matter got filed
      // under the wrong year forever.
      name: name.trim(),
      leadUserId,
      classification,
    });
    onDone();
  });

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void invoke();
    },
    [invoke],
  );

  // Prospects are waited for too, not just tolerated when they arrive late.
  // Without this the picker can render before they load, the Prospects group
  // is absent, and whoever opened the dropdown concludes a prospect cannot be
  // used here. A failed prospect fetch does NOT block the form — an existing
  // client is still a perfectly good engagement — so "not loading" rather
  // than "ready".
  const ready =
    orgs.resource.state === "ready" &&
    people.resource.state === "ready" &&
    prospects.resource.state !== "loading";

  // A Lead must be firm-side and not suspended. Suspended is excluded for the
  // same reason: app.reach returns 0 for a suspended user, so naming one
  // would create an engagement whose Lead cannot reach it.
  const leadCandidates =
    people.resource.state === "ready"
      ? people.resource.data.filter((p) => p.role !== "client" && !p.isSuspended)
      : [];

  return (
    <form onSubmit={submit} className="mt-6 border border-line-strong p-6">
      <Label as="h2">Start an engagement</Label>

      {!ready && <SkeletonRows rows={4} />}

      {orgs.resource.state === "ready" &&
        (orgs.resource.data.length === 0 || addingClient) && (
          <NewOrganisation
            onCreated={() => {
              orgs.reload();
              setAddingClient(false);
            }}
          />
        )}

      {ready && orgs.resource.state === "ready" && orgs.resource.data.length > 0 && (
        <>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            {/* The new-client escape hatch lives in the dropdown itself.
                It was a small text toggle underneath, and the user looked at
                this form and reported there was no way to do anything but
                choose an existing client — which is exactly what a control
                nobody notices amounts to. People look in the list. */}
            {/* Two groups, because they are two different things and picking
                one of them has a consequence the other does not. An existing
                client is just a reference. A PROSPECT becomes a client the
                moment this form is submitted — its permanent filing code is
                minted, its contacts become client contacts — so the group is
                labelled with that consequence rather than quietly listing
                them alongside. */}
            <Select
              label="Client"
              hint={
                prospectId
                  ? "Starting this engagement makes them a client."
                  : "Not listed? Choose the last option to add them."
              }
              value={
                addingClient
                  ? NEW_CLIENT
                  : prospectId
                    ? `${PROSPECT_PREFIX}${prospectId}`
                    : organisationId
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === NEW_CLIENT) {
                  setAddingClient(true);
                  setOrganisationId("");
                  setProspectId("");
                } else if (value.startsWith(PROSPECT_PREFIX)) {
                  setAddingClient(false);
                  setOrganisationId("");
                  setProspectId(value.slice(PROSPECT_PREFIX.length));
                } else {
                  setAddingClient(false);
                  setProspectId("");
                  setOrganisationId(value);
                }
              }}
              required
            >
              <option value="">Choose…</option>
              <optgroup label="Clients">
                {orgs.resource.data.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </optgroup>
              {openProspects.length > 0 && (
                <optgroup label="Prospects — starting an engagement makes them a client">
                  {openProspects.map((p) => (
                    <option key={p.id} value={`${PROSPECT_PREFIX}${p.id}`}>
                      {p.name}
                      {p.sector ? ` · ${p.sector}` : ""}
                    </option>
                  ))}
                </optgroup>
              )}
              <option value={NEW_CLIENT}>+ Add a new client…</option>
            </Select>

            <Select
              label="Lead"
              hint="Who runs this engagement day to day."
              value={leadUserId}
              onChange={(e) => setLeadUserId(e.target.value)}
              required
            >
              <option value="">Choose…</option>
              {/* Firm-side only. /api/people deliberately returns everyone,
                  including clients, so the People screen can show somebody
                  the moment they accept an invitation — the caller filters.
                  This one did not, so a client appeared in the Lead list.
                  Naming a client as Lead would be refused by the membership
                  policy anyway, but offering it is a misleading control and
                  the refusal would arrive at the end of a filled-in form. */}
              {leadCandidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName} · {roleLabel(p.role)}
                </option>
              ))}
            </Select>
          </div>

          <div className="mt-5">
            <Field
              label="Name"
              hint="What this matter is called. The filing reference is assigned automatically."
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="off"
            />
          </div>

          <div className="mt-5 max-w-sm">
            <Select
              label="Visibility"
              hint="Sets the highest visibility allowed for information added to this engagement. You can make it more private later."
              value={classification}
              onChange={(e) => setClassification(e.target.value as Classification)}
            >
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {classificationLabel(c)}
                </option>
              ))}
            </Select>
          </div>

          {refusal && (
            <p className="mt-5 border-l-2 border-danger pl-4 text-sm text-ink">
              {refusal.message}
            </p>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <Button tone="primary" type="submit" loading={pending}>
              Start it
            </Button>
            <Button tone="quiet" type="button" onClick={onCancel}>
              Cancel
            </Button>
          </div>

          <p className="mt-5 max-w-[58ch] text-sm text-secondary">
            It starts as a draft. Nothing is visible to the client until a
            portal is readied and activated, which are two separate deliberate
            acts.
          </p>
        </>
      )}
    </form>
  );
}

/**
 * The first-run case. An engagement needs an organisation to belong to, and on
 * a fresh platform there are none — so rather than present a picker with
 * nothing in it and no way forward, the form becomes the one for adding the
 * first client.
 */
function NewOrganisation({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [country, setCountry] = useState("");

  const { invoke, pending, refusal } = useAction(async () => {
    await api.post<{ id: string }>("/api/organisations", {
      name: name.trim(),
      sector: sector.trim() || undefined,
      country: country.trim() || undefined,
    });
    setName("");
    setSector("");
    setCountry("");
    onCreated();
  });

  return (
    <div className="mt-5 border-t border-line pt-5">
      <p className="max-w-[58ch] text-sm text-secondary">
        There are no client organisations yet. An engagement belongs to one, so
        add the client first.
      </p>

      <div className="mt-5 grid gap-5 sm:grid-cols-3">
        <Field
          label="Organisation"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
        <Field
          label="Sector"
          hint="Optional."
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          autoComplete="off"
        />
        <Field
          label="Country"
          hint="Optional."
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          autoComplete="off"
        />
      </div>

      {refusal && (
        <p className="mt-5 border-l-2 border-danger pl-4 text-sm text-ink">
          {refusal.message}
        </p>
      )}

      <Button
        tone="secondary"
        type="button"
        className="mt-6"
        loading={pending}
        disabled={!name.trim()}
        onClick={() => void invoke()}
      >
        Add this client
      </Button>
    </div>
  );
}
